/**
 * Mermaid flowchart → DiagramData importer (pure, unit-tested).
 *
 * Supports a practical subset of Mermaid flowchart syntax:
 *   Header:  `flowchart TD|LR|TB|RL|BT`, `graph TD|LR|...`
 *   Nodes:   A[Rect]  A(Round)  A{Diamond}  A((Circle))
 *   Edges:   A-->B    A-- text -->B    A-->|text|B    A---B
 *
 * Unsupported lines are ignored gracefully. Node ids are taken verbatim from
 * the Mermaid source; labels come from the bracket text (falling back to the id
 * when a node is only referenced, never defined with brackets).
 */

import type { DiagramData, DiagramNode, DiagramEdge, NodeStyle } from '../types/schema'

type ShapeKind = NonNullable<NodeStyle['shape']>

interface ParsedNode {
  id: string
  label: string
  shape: ShapeKind
  nodeType: DiagramNode['nodeType']
}

const DIRECTIONS = new Set(['TD', 'TB', 'BT', 'LR', 'RL'])

/** Map a Mermaid bracket pair to a shape + a sensible flowchart nodeType. */
function shapeForBrackets(open: string): { shape: ShapeKind; nodeType: DiagramNode['nodeType'] } {
  switch (open) {
    case '((':
      return { shape: 'circle', nodeType: 'start' }
    case '(':
      return { shape: 'rounded', nodeType: 'process' }
    case '{':
      return { shape: 'diamond', nodeType: 'decision' }
    case '[':
    default:
      return { shape: 'rectangle', nodeType: 'process' }
  }
}

/**
 * Node-definition matcher. Finds occurrences of `id<bracket>text<bracket>` in a
 * line. Returns the brackets so we can pick the shape. Handles `((`/`)` order
 * by trying the longest opener first.
 */
const NODE_DEF_RE = /([A-Za-z0-9_]+)\s*(\(\(|\(|\[|\{)([^\]})]*?)(\)\)|\)|\]|\})/g

/** Parse a single line for node definitions, registering them in the map. */
function collectNodeDefs(line: string, nodes: Map<string, ParsedNode>) {
  NODE_DEF_RE.lastIndex = 0
  let m: RegExpExecArray | null
  while ((m = NODE_DEF_RE.exec(line)) !== null) {
    const id = m[1]
    const open = m[2]
    const text = m[3].trim()
    const { shape, nodeType } = shapeForBrackets(open)
    nodes.set(id, {
      id,
      label: text || id,
      shape,
      nodeType,
    })
  }
}

/** Ensure a referenced-but-undefined node exists with default styling. */
function ensureNode(id: string, nodes: Map<string, ParsedNode>) {
  if (!nodes.has(id)) {
    nodes.set(id, { id, label: id, shape: 'rectangle', nodeType: 'process' })
  }
}

/** Strip `id[...]`/`id(...)`/`id{...}`/`id((...))` bracket bodies down to `id`. */
function stripNodeBrackets(line: string): string {
  return line.replace(NODE_DEF_RE, (_full, id) => id)
}

/**
 * Edge matcher, run AFTER node-def brackets have been stripped from the line.
 * Supports:
 *   A-->B
 *   A---B            (no arrowhead)
 *   A-- text -->B    (inline mid-text)
 *   A-->|text|B      (pipe label)
 */
const EDGE_RE =
  /^\s*([A-Za-z0-9_]+)\s*-{2,3}(?:\s*([^->|]+?)\s*-{2,3})?>?\s*(?:\|([^|]*)\|)?\s*([A-Za-z0-9_]+)\s*$/

function parseEdge(strippedLine: string): { source: string; target: string; label?: string } | null {
  const m = EDGE_RE.exec(strippedLine)
  if (!m) return null
  const source = m[1]
  const midText = m[2]?.trim()
  const pipeText = m[3]?.trim()
  const target = m[4]
  const label = pipeText || midText || undefined
  if (!source || !target) return null
  return { source, target, label }
}

export interface MermaidImportResult {
  data: DiagramData
  warnings: string[]
}

/**
 * Parse Mermaid flowchart text into DiagramData. Always returns a valid
 * DiagramData (possibly empty). Collects non-fatal warnings for skipped lines.
 */
export function parseMermaidToDiagram(input: string): MermaidImportResult {
  const warnings: string[] = []
  const nodes = new Map<string, ParsedNode>()
  const edges: DiagramEdge[] = []

  const rawLines = input.split(/\r?\n/)

  // Some Mermaid uses `;` as a statement separator on a single line.
  const lines: string[] = []
  for (const raw of rawLines) {
    raw.split(';').forEach((part) => {
      const t = part.trim()
      if (t) lines.push(t)
    })
  }

  let sawHeader = false
  for (const line of lines) {
    if (line.startsWith('%%')) continue // comment

    // Header: `flowchart TD` / `graph LR`
    const headerMatch = /^(flowchart|graph)\s+([A-Za-z]{2})\b/.exec(line)
    if (headerMatch) {
      sawHeader = true
      const dir = headerMatch[2].toUpperCase()
      if (!DIRECTIONS.has(dir)) {
        warnings.push(`Unknown direction "${dir}", defaulting to TB`)
      }
      continue
    }

    // Bare `flowchart` / `graph` with no/unknown direction.
    if (/^(flowchart|graph)\b/.test(line)) {
      sawHeader = true
      continue
    }

    // Always collect any node definitions present on the line (edge lines may
    // define their endpoints inline, e.g. `A[Start]-->B{Choice}`).
    const hadNodeDef = /[A-Za-z0-9_]+\s*(\(\(|\(|\[|\{)/.test(line)
    collectNodeDefs(line, nodes)

    // Strip bracket bodies so the edge matcher sees plain `id`s.
    const stripped = stripNodeBrackets(line)
    const edge = parseEdge(stripped)
    if (edge) {
      ensureNode(edge.source, nodes)
      ensureNode(edge.target, nodes)
      edges.push({
        id: `edge-${edge.source}-${edge.target}-${edges.length}`,
        source: edge.source,
        target: edge.target,
        label: edge.label,
        type: 'custom',
      })
      continue
    }

    // A standalone node definition line (no edge) is fine — already collected.
    if (hadNodeDef) {
      continue
    }

    // Otherwise: unsupported line, ignore gracefully.
    if (line.trim()) {
      warnings.push(`Skipped unsupported line: "${line}"`)
    }
  }

  if (!sawHeader) {
    warnings.push('No flowchart/graph header found — parsed best-effort.')
  }

  const diagramNodes: DiagramNode[] = Array.from(nodes.values()).map((n) => ({
    id: n.id,
    label: n.label,
    nodeType: n.nodeType,
    style: { shape: n.shape },
  }))

  const data: DiagramData = {
    diagramType: 'flowchart',
    nodes: diagramNodes,
    edges,
    layout: { algorithm: 'dagre', direction: 'TB' },
  }

  return { data, warnings }
}
